import {DeliveryMethod, SaleCancelReason} from '@web/core/constants'
import {AssignmentType, DeliveryPaymentType, StatusType} from '@web/slices/productBundle/slice'

/**
 * @see https://oss.fin.navercorp.com/payOrder/order-manager/issues/13813#issuecomment-1926411
 **/
export const DeliveryMethodNameMap: Record<string, string> = {
    [DeliveryMethod.DELIVERY]: '직접 택배사에서 발송',
    [DeliveryMethod.VISIT_RECEIPT]: '방문수령',
    [DeliveryMethod.QUICK_SVC]: '퀵서비스',
    [DeliveryMethod.DIRECT_DELIVERY]: '직접전달',
    [DeliveryMethod.NOTHING]: '직거래',
}

/** 카페 주문에서 활용, 추후에는 활용처가 없을듯 */
export const DeliveryMethodNameMapWithCafe: Record<string, string> = {
    [DeliveryMethod.DELIVERY]: '택배, 등기, 소포',
    [DeliveryMethod.VISIT_RECEIPT]: '방문수령',
    [DeliveryMethod.QUICK_SVC]: '퀵서비스',
    [DeliveryMethod.DIRECT_DELIVERY]: '직접전달',
    [DeliveryMethod.NOTHING]: '배송없음',
}

export const SaleCancelReasonNameMap: Record<string, string> = {
    [SaleCancelReason.NOT_YET_DISCUSSION]: '상호 협의가 완료되지 않은 주문건',
    [SaleCancelReason.OUT_OF_STOCK]: '재고부족으로 인한 판매불가',
    [SaleCancelReason.SALE_INTENT_CHANGED]: '판매의사 변심으로 인한 거부',
    [SaleCancelReason.NOT_YET_PAYMENT]: '구매자의 미결제로 인한 거부',
}

export const DeliveryPaymentTypeMap = {
    PREPAYED: DeliveryPaymentType.PREPAID,
    CASH_ON_DELIVERY: DeliveryPaymentType.CASH_ON_DELIVERY,
    FREE: DeliveryPaymentType.FREE,
    CASH_ON_DELIVERY_OR_PREPAYED: DeliveryPaymentType.UNKNOWN,
    UNKNOWN: DeliveryPaymentType.UNKNOWN,
}

/** @remarks 노출되는 text와 type은 1:1 대응이 아님 */
export const SaleHistoryProductStatusMap = {
    /** 결제대기중 */
    PAYMENT_WAITING: StatusType.PAYMENT_WAITING,
    /** 미결제취소완료 */
    CANCELED_BY_NOPAYMENT: StatusType.UNPAID_CANCELLATION,
    /** 배송중 */
    DELIVERING: StatusType.DELIVERING,
    /** 배송완료 */
    DELIVERED: StatusType.DELIVERED,
    /** 구매확정완료 */
    PURCHASE_DECIDED: StatusType.PURCHASE_DECIDED,
    /** 취소완료 */
    CANCELED: StatusType.CANCELED,
    /** 반품환불완료 */
    RETURNED: StatusType.RETURN_REFUND,
    /** 교환완료 */
    EXCHANGED: StatusType.EXCHANGED,
    /** 결제완료 */
    PAYED: StatusType.PAYED,
    /** 알수 없음 */
    PAYMENT_ERROR: StatusType.UNKNOWN,
}

export const SaleDetailProductStatusMap = {
    /** 결제완료 */
    PAYMENT_COMPLETED: StatusType.PAYED,
    /** 결제대기중 */
    PAYMENT_WAITING: StatusType.PAYMENT_WAITING,
    /** 취소요청 */
    CANCEL_REQUESTED: StatusType.CANCEL_REQUEST,
    /** 취소중 */
    CANCELING: StatusType.CANCELING,
    /** 취소완료 */
    CANCELLED: StatusType.CANCELED,
    /** 배송준비중 */
    DELIVERY_PREPARING: StatusType.DELIVERY_READY,
    /** 배송완료 */
    DELIVERED: StatusType.DELIVERED,
    /** 발송지연 */
    DISPATCH_DELAYED: StatusType.DELAYED_DISPATCH,
    /** 배송준비중 */
    DELIVERING_BEFORE: StatusType.DELIVERY_READY,
    /** 배송중 */
    DELIVERING: StatusType.DELIVERING,
    /** 미결제취소완료 */
    CANCELLED_BY_NO_PAYMENT: StatusType.UNPAID_CANCELLATION,
    /** 반품요청 */
    REFUND_REQUESTED: StatusType.RETURN_REQUEST,
    /** 반품수거완료 */
    REFUND_PICKUP_COMPLETED: StatusType.RETURN_PICKED_UP,
    /** 반품환불완료 */
    REFUND_COMPLETED: StatusType.RETURN_REFUND,
    /** 구매확정완료 */
    PURCHASE_CONFIRMED: StatusType.PURCHASE_DECIDED,
    /** 구매확정연장 */
    PURCHASE_CONFIRM_EXTENDED: StatusType.PURCHASE_CONFIRM_EXTENDED,
    /** 직권취소중 */
    CANCELING_BY_ADMIN: StatusType.CANCELING_BY_ADMIN,
    /** 직권취소완료 */
    CANCELLED_BY_ADMIN: StatusType.CANCELLED_BY_ADMIN,
    /** 부분취소 */
    PARTIALLY_CANCELLED: StatusType.PARTIALLY_CANCELLED,
    /** 반품중 */
    REFUNDING: StatusType.RETURNING,
    /** 교환요청 */
    EXCHANGE_REQUESTED: StatusType.EXCHANGE_REQUEST,
    /** 교환중 */
    EXCHANGING: StatusType.EXCHANGING,
    /** 교환수거완료 */
    EXCHANGE_PICKUP_COMPLETED: StatusType.RETURN_PICKED_UP,
    /** 교환재배송중 */
    EXCHANGE_DELIVERING: StatusType.EXCHANGE_DELIVERING,
    /** 교환완료 */
    EXCHANGE_COMPLETED: StatusType.EXCHANGED,
}

export const AssignmentTypeMap = {
    PLACE_ORDER: AssignmentType.CHECK_ORDER,
    DISPATCH: AssignmentType.DISPATCH,
    CANCEL_BY_SELLER: AssignmentType.SALE_REJECT,
    CANCEL_ADMISSION: AssignmentType.CANCEL_ADMISSION,
    CANCEL_REJECT: AssignmentType.CANCEL_REJECT,
    DELIVERY_INFO: AssignmentType.TRACK_DELIVERY,
    RETURN_ADMISSION: AssignmentType.RETURN_ADMISSION,
    RETURN_REJECT: AssignmentType.RETURN_REJECT,
    COLLECT_APPROVAL: AssignmentType.COLLECTION_ADMISSION,
    DELIVERY_DESIGNATED_RESERVATION: AssignmentType.BOOKING_VISIT_DELIVERY_DISPATCH,
    CONVENIENCE_DELIVERY_RESERVATION: AssignmentType.BOOKING_CONVENIENCE_STORE_DELIVERY,
}

export const NeloLoggingCase = {
    INVALID_ASSIGNMENT_PATH: 'INVALID_ASSIGNMENT_PATH',
} as const

/** nLog 코드 */
export const NLogMap = {
    FILTER_BUTTON: {
        PAY_WAIT: 'fitSetPayWait',
        NEW_ORDERS: 'fitSetPayed',
        DELIVERY: 'fitSetDelivering',
        DELIVERED: 'fitSetDelivered',
        PURCHASE_DECIDED: 'fitSetPurDec',
        CANCEL: 'fitSetCancelAll',
        RETURN: 'fitSetReturnAll',
    },
    ASSIGNMENT_BUTTON: {
        [AssignmentType.SALE_REJECT]: 'salesReject',
        [AssignmentType.DISPATCH]: 'dispTreat',
        [AssignmentType.BOOKING_VISIT_DELIVERY_DISPATCH]: 'deliveryReserve',
        [AssignmentType.BOOKING_VISIT_DELIVERY_CANCEL_REJECT]: '',
        [AssignmentType.BOOKING_CONVENIENCE_STORE_DELIVERY]: 'cvsDeliveryReserve',
        [AssignmentType.CANCEL_REJECT]: 'cancelRej',
        [AssignmentType.RETURN_ADMISSION]: 'returnApp',
        [AssignmentType.RETURN_REJECT]: 'returnRej',
        [AssignmentType.CHECK_ORDER]: 'ordConf',
        [AssignmentType.TRACK_DELIVERY]: 'tracking',
        [AssignmentType.COLLECTION_ADMISSION]: 'pickupApp',
        [AssignmentType.CANCEL_ADMISSION]: 'cancelApp',
        [AssignmentType.RELOAD]: 'reload',
    },
}

export const SearchPlaceholder = '상품명/닉네임명 입력'
